
import { Component, Vue } from 'vue-property-decorator'
import { getToken } from '@/utils/cookies'
import request from '@/utils/request'

@Component({
  name: 'NotificationLogs'
})
export default class extends Vue {
  private listLoading = false
  private testLoading = false
  private logs: any[] = []
  private total = 0
  private startDate = ''
  private endDate = ''
  private detailsDialogVisible = false
  private testDialogVisible = false
  private selectedLog: any = {}

  private stats = {
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    last24Hours: 0,
    last7Days: 0,
    byType: {} as Record<string, number>,
    byProvider: {} as Record<string, number>
  }

  private listQuery = {
    page: 1,
    pageSize: 50,
    status: '',
    notificationType: '',
    userId: ''
  }

  private testForm = {
    userId: '',
    title: '',
    message: ''
  }

  created() {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)
    this.startDate = start.toISOString().split('T')[0]
    this.endDate = end.toISOString().split('T')[0]
    this.loadLogs()
    this.loadStats()
  }

  private async loadStats() {
    try {
      const response: any = await request({
        url: '/admin/notifications/stats',
        method: 'get',
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      })

      const res = response.data || response
      if (res.status && (res.status.code === 'success' || res.status.code === 0)) {
        this.stats = res.data
      }
    } catch (error) {
      console.error('Failed to load stats:', error)
    }
  }

  private async loadLogs() {
    this.listLoading = true
    try {
      const params: any = {
        page: this.listQuery.page,
        pageSize: this.listQuery.pageSize
      }

      if (this.startDate) {
        params.startDate = this.startDate
      }
      if (this.endDate) {
        params.endDate = this.endDate
      }
      if (this.listQuery.status) params.status = this.listQuery.status
      if (this.listQuery.notificationType) params.notificationType = this.listQuery.notificationType
      if (this.listQuery.userId) params.userId = this.listQuery.userId

      const response: any = await request({
        url: '/admin/notifications/logs',
        method: 'get',
        params,
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      })

      const res = response.data || response
      if (res.status && (res.status.code === 'success' || res.status.code === 0)) {
        this.logs = res.data.logs
        this.total = res.data.pagination.total
      }
    } catch (error) {
      console.error('Failed to load logs:', error)
      this.$message.error('Failed to load notification logs')
    } finally {
      this.listLoading = false
    }
  }

  private async retryNotification(row: any) {
    try {
      const response: any = await request({
        url: `/admin/notifications/${row.id}/retry`,
        method: 'post',
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      })

      const res = response.data || response
      if (res.status && (res.status.code === 'success' || res.status.code === 0)) {
        this.$message.success('Notification sent successfully')
        this.loadLogs()
        this.loadStats()
      } else {
        this.$message.error(res.status?.message || 'Failed to retry notification')
      }
    } catch (error: any) {
      this.$message.error(error.message || 'Failed to retry notification')
    }
  }

  private showTestDialog() {
    this.testForm = { userId: '', title: '', message: '' }
    this.testDialogVisible = true
  }

  private async sendTestNotification() {
    if (!this.testForm.userId) {
      this.$message.warning('Please enter a User ID')
      return
    }

    this.testLoading = true
    try {
      const response: any = await request({
        url: '/admin/notifications/test',
        method: 'post',
        data: this.testForm,
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      })

      const res = response.data || response
      if (res.status && (res.status.code === 'success' || res.status.code === 0)) {
        this.$message.success('Test notification sent successfully')
        this.testDialogVisible = false
        this.loadLogs()
        this.loadStats()
      } else {
        this.$message.error(res.status?.message || 'Failed to send notification')
      }
    } catch (error: any) {
      this.$message.error(error.message || 'Failed to send notification')
    } finally {
      this.testLoading = false
    }
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  private formatNotificationType(type: string): string {
    const typeMap: { [key: string]: string } = {
      subscription_expiry_30d: 'Expiry 30D',
      subscription_expiry_7d: 'Expiry 7D',
      subscription_expiry_3d: 'Expiry 3D',
      subscription_expiry_1d: 'Expiry 1D',
      subscription_expired: 'Expired',
      admin_test: 'Admin Test'
    }
    return typeMap[type] || type
  }

  private getNotificationTypeTag(type: string): string {
    const typeMap: { [key: string]: string } = {
      subscription_expiry_30d: 'info',
      subscription_expiry_7d: 'warning',
      subscription_expiry_3d: 'warning',
      subscription_expiry_1d: 'danger',
      subscription_expired: 'danger',
      admin_test: 'primary'
    }
    return typeMap[type] || 'info'
  }

  private getStatusTag(status: string): string {
    const statusMap: { [key: string]: string } = {
      sent: 'success',
      failed: 'danger',
      pending: 'warning'
    }
    return statusMap[status] || 'info'
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.loadLogs()
  }

  private handleRefresh() {
    this.loadLogs()
    this.loadStats()
  }

  private handleSizeChange(val: number) {
    this.listQuery.pageSize = val
    this.loadLogs()
  }

  private handleCurrentChange(val: number) {
    this.listQuery.page = val
    this.loadLogs()
  }

  private showDetails(row: any) {
    this.selectedLog = row
    this.detailsDialogVisible = true
  }
}
